"use client"
import {connect} from "react-redux";
import { selectAuthState } from "../../../../../store/selectors/authSelectors"
import React, { useEffect, useState, useMemo } from "react";
import { Button } from "../../../../components/ui/button";
import "../../../../components/ui/input";
import { Loader2, AlertCircle, RefreshCw, Users, Save, Send, CheckSquare } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Card, CardContent } from "../../../../components/ui/card";
import "../../../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/table";
import { Checkbox } from "../../../../components/ui/checkbox";

// Import components
import { SupplierFilterBar } from "./components/SupplierFilterBar";

// Import the assignment API functions
import { assignItemsToSuppliers, getItemsWithAssignedSuppliers, removeItemSupplierAssignment, getSupplierAssignmentStats, sendRFQToSupplier, createQuotationsForSuppliers } from "../../../../../ApiCalls/mantisFlowsManangment";

// Simple Supplier Card Component
const SimpleSupplierCard = ({ supplier, isSelected, onToggle, assignedItemsCount = 0 }) => {
  return (
    <Card 
      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected 
          ? 'ring-2 ring-blue-500 border-blue-200 bg-blue-50 dark:bg-blue-900/20' 
          : 'hover:border-slate-300 dark:hover:border-slate-600'
      }`}
      onClick={() => onToggle(supplier.id)}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
              isSelected ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
            }`}>
              {supplier.name?.charAt(0)?.toUpperCase() || 'S'}
            </div>
            <div>
              <h3 className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate max-w-32" title={supplier.name}>
                {supplier.name}
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-32" title={supplier.email}>
                {supplier.email || supplier.contact_email || 'No email'}
              </p>
            </div>
          </div>
          <div className="text-right">
            {assignedItemsCount > 0 && (
              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                {assignedItemsCount} items
              </Badge>
            )}
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {supplier.country || 'Unknown'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

function SuppliersSection ({ 
  data, 
  mantisBusiness,
  suppliers = [], 
  orderItems = [], 
  onSupplierSelect, 
  selectedSuppliersIds = [],
  onSuppliersSelect,
  isLoading = false,
  isLoadingItems = false,
  error = null,
  onRefresh,
  currentUser,
  addLog,
  clientId,
  onItemsUpdated
})  {
  // Estados para filtros de suppliers
  const [searchText, setSearchText] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [minRating, setMinRating] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [localSelectedSuppliers, setLocalSelectedSuppliers] = useState(
    selectedSuppliersIds || []
  );

  // Estados para paginación de suppliers
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  // Estados para asignación de suppliers (copiado de RequestSection)
  const [itemSupplierAssignments, setItemSupplierAssignments] = useState({});
  const [isAssigning, setIsAssigning] = useState(false);
  const [, setAssignmentStats] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [isSendingEmails, setIsSendingEmails] = useState(false);

  // Estado para el supplier seleccionado y los items seleccionados por supplier
  const [supplierItemsSelection, setSupplierItemsSelection] = useState({});

  // Items data
  const itemsData = Array.isArray(orderItems) ? orderItems : [];
  const hasItems = itemsData.length > 0;

  // Sincronizar con las props
  useEffect(() => {
    setLocalSelectedSuppliers(selectedSuppliersIds || []);
  }, [selectedSuppliersIds]);

  // Load existing assignments when component mounts or data changes
  useEffect(() => {
    if (clientId && data?.id && hasItems) {
      loadItemSupplierAssignments();
      loadAssignmentStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, data?.id, hasItems]);

  // Load existing supplier assignments
  const loadItemSupplierAssignments = async () => {
    try {
      const response = await getItemsWithAssignedSuppliers(clientId, data.id);
      const assignments = {};

      if (response.data && Array.isArray(response.data)) {
        response.data.forEach((item) => {
          if (item.assigned_supplier_id) {
            assignments[item.id] = item.assigned_supplier_id;
          }
        });
      }

      setItemSupplierAssignments(assignments);
    } catch (error) {
      console.error("Error loading supplier assignments:", error);
    }
  };

  // Load assignment statistics
  const loadAssignmentStats = async () => {
    try {
      const response = await getSupplierAssignmentStats(clientId, data.id);
      setAssignmentStats(response.data);
    } catch (error) {
      console.error("Error loading assignment stats:", error);
    }
  };

  // Handle supplier assignment change
  
  // Save all supplier assignments
  const saveSupplierAssignments = async () => {
    if (!hasUnsavedChanges) return;

    setIsAssigning(true);
    try {
      const assignments = Object.entries(itemSupplierAssignments).map(
        ([itemId, supplierId]) => ({
          itemId: parseInt(itemId),
          supplierId: parseInt(supplierId),
        })
      );

      await assignItemsToSuppliers(
        clientId,
        data.id,
        assignments,
        currentUser?.id
      );

      setHasUnsavedChanges(false);
      await loadAssignmentStats();

      if (onItemsUpdated) {
        onItemsUpdated();
      }

      addLog?.(
        `Supplier assignments saved successfully for ${assignments.length} items`,
        "success"
      );
    } catch (error) {
      console.error("Error saving assignments:", error);
      addLog?.(`Error saving supplier assignments: ${error.message}`, "error");
    } finally {
      setIsAssigning(false);
    }
  };

  // Send emails to assigned suppliers
  const sendEmailsToAssignedSuppliers = async () => {
    try {
      setIsSendingEmails(true);

      // Si hay cambios sin guardar, primero guarda las asignaciones
      if (hasUnsavedChanges) {
        await saveSupplierAssignments();
      }

      // Construir suppliersWithItems usando supplierItemsSelection
      const suppliersWithItems = selectedSuppliersIds
        .map((supplierId) => {
          const supplier = suppliers.find(
            (s) => String(s.id) === String(supplierId)
          );
          if (!supplier) return null;

          // Obtener items seleccionados para este supplier
          const selectedItemIds =
            supplierItemsSelection[supplierId] || new Set();
          const assignedItems = orderItems.filter((item) =>
            selectedItemIds.has(item.id)
          );

          if (assignedItems.length === 0) return null;

          return {
            id: supplier.id,
            name: supplier.name,
            phone: supplier.phone,
            email: supplier.email || supplier.contact_email,
            items: assignedItems,
          };
        })
        .filter(Boolean);

      if (suppliersWithItems.length === 0) {
        addLog(
          "No hay items seleccionados para enviar RFQs. Por favor selecciona al menos un item para cada supplier.",
          "warning"
        );
        return;
      }

      console.log("Enviando RFQs a suppliers:", suppliersWithItems);

      // Preparar las opciones para el envío
      const options = {
        clientName: mantisBusiness?.name || "Cliente",
        subject: ``,
        message: "",
        attachments: [],
      };

      // Enviar RFQs
      const response = await sendRFQToSupplier(
        clientId,
        data.id,
        suppliersWithItems,
        options
      );

      // Crear cotizaciones para los suppliers seleccionados
      const supplierIds = suppliersWithItems.map((supplier) => supplier.id);
      try {
        await createQuotationsForSuppliers(
          clientId,
          data.id,
          supplierIds,
          suppliersWithItems
        );
        addLog(
          `Cotizaciones creadas exitosamente para ${supplierIds.length} suppliers`,
          "success"
        );
      } catch (quotationError) {
        console.error("Error creating quotations:", quotationError);
        addLog(
          `RFQs enviados pero error al crear cotizaciones: ${quotationError.message}`,
          "warning"
        );
      }

      handleRFQSent(response);
      addLog(
        `RFQs enviados exitosamente a ${suppliersWithItems.length} suppliers`,
        "success"
      );
    } catch (error) {
      console.error("Error al enviar RFQs:", error);
      addLog(
        "Error al enviar RFQs: " + (error.message || "Error desconocido"),
        "error"
      );
    } finally {
      setIsSendingEmails(false);
    }
  };

  // Remove assignment for a specific item
  
  // Calculate assignment statistics
  const assignedItemsCount = Object.keys(itemSupplierAssignments).length;
  const totalItemsCount = itemsData.length;
  const unassignedItemsCount = totalItemsCount - assignedItemsCount;
  const assignedSuppliersCount = new Set(Object.values(itemSupplierAssignments))
    .size;

  // Calculate items selected in current supplier selection
  const totalSelectedItems = Object.values(supplierItemsSelection).reduce(
    (total, items) => {
      return total + (items ? items.size : 0);
    },
    0
  );

  // Check if any suppliers have items selected
  const hasSelectedItems = selectedSuppliersIds.some(
    (supplierId) =>
      supplierItemsSelection[supplierId] &&
      supplierItemsSelection[supplierId].size > 0
  );

  // Get assignments count per supplier
  
  // Obtener lista única de países
  const countries = useMemo(() => {
    const countrySet = new Set();
    suppliers.forEach((supplier) => {
      if (supplier.country) {
        countrySet.add(supplier.country);
      }
    });
    return Array.from(countrySet).sort();
  }, [suppliers]);

  // Filtrar y ordenar proveedores
  const filteredAndSortedSuppliers = useMemo(() => {
    let filtered = Array.isArray(suppliers)
      ? suppliers.filter((supplier) => {
          if (!supplier) return false;

          const searchLower = searchText.toLowerCase();
          const matchesSearch =
            !searchText ||
            (supplier.name?.toLowerCase() || "").includes(searchLower) ||
            (supplier.contact_email?.toLowerCase() || "").includes(
              searchLower
            ) ||
            (supplier.email?.toLowerCase() || "").includes(searchLower) ||
            (supplier.country?.toLowerCase() || "").includes(searchLower);

          const matchesCountry =
            !selectedCountry || supplier.country === selectedCountry;
          const matchesFavorites = !showFavoritesOnly || supplier.is_favorite;
          const matchesRating =
            !minRating ||
            (supplier.rating_overall && supplier.rating_overall >= minRating);

          return (
            matchesSearch && matchesCountry && matchesFavorites && matchesRating
          );
        })
      : [];

    filtered.sort((a, b) => {
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;

      let valueA, valueB;

      switch (sortBy) {
        case "rating":
          valueA = a.rating_overall || 0;
          valueB = b.rating_overall || 0;
          break;
        case "country":
          valueA = a.country || "";
          valueB = b.country || "";
          break;
        case "leadtime":
          valueA = a.typicalleadtime || "";
          valueB = b.typicalleadtime || "";
          break;
        case "name":
        default:
          valueA = a.name || "";
          valueB = b.name || "";
          break;
      }

      if (typeof valueA === "string") {
        return sortOrder === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else {
        return sortOrder === "asc" ? valueA - valueB : valueB - valueA;
      }
    });

    return filtered;
  }, [
    suppliers,
    searchText,
    selectedCountry,
    showFavoritesOnly,
    minRating,
    sortBy,
    sortOrder,
  ]);

  // Calcular elementos para la página actual
  const totalPages = Math.ceil(
    filteredAndSortedSuppliers.length / itemsPerPage
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSuppliers = filteredAndSortedSuppliers.slice(
    startIndex,
    endIndex
  );

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchText,
    selectedCountry,
    showFavoritesOnly,
    minRating,
    sortBy,
    sortOrder,
  ]);

  // Handle item checkbox
  
  
  // Maneja el cambio de selección de un item para el supplier activo
  
  const handleClearFilters = () => {
    setSearchText("");
    setSelectedCountry("");
    setMinRating(null);
    setShowFavoritesOnly(false);
  };

  const handleAddSupplier = () => {
    console.log("Add supplier");
  };

  const handleRFQSent = (response) => {
    console.log("RFQ sent successfully:", response);
  };

  const activeFiltersCount = [
    searchText,
    selectedCountry,
    minRating,
    showFavoritesOnly,
  ].filter(Boolean).length;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[600px] p-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Loading suppliers...
          </span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[600px] p-8">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
            Failed to Load Suppliers
          </h3>
          <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
          {onRefresh && (
            <Button
              onClick={onRefresh}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Assignment Statistics and Actions */}
      {hasItems && suppliers.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                    Supplier Assignments
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {assignedItemsCount} of {totalItemsCount} items assigned to{" "}
                    {assignedSuppliersCount} suppliers
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {assignedItemsCount}
                  </div>
                  <div className="text-xs text-green-700 dark:text-green-300">
                    Assigned
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
                    {unassignedItemsCount}
                  </div>
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    Pending
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {assignedSuppliersCount}
                  </div>
                  <div className="text-xs text-blue-700 dark:text-blue-300">
                    Suppliers
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {hasUnsavedChanges && (
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700"
                >
                  Unsaved Changes
                </Badge>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={saveSupplierAssignments}
                disabled={!hasUnsavedChanges || isAssigning}
                className="border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20"
              >
                {isAssigning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Assignments
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={sendEmailsToAssignedSuppliers}
                disabled={
                  isSendingEmails ||
                  selectedSuppliersIds.length === 0 ||
                  !hasSelectedItems
                }
                className="border-purple-200 text-purple-600 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20"
              >
                {isSendingEmails ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send RFQs ({totalSelectedItems} items)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div>
        <SupplierFilterBar
          searchText={searchText}
          onSearchChange={setSearchText}
          selectedCountry={selectedCountry}
          onCountryChange={setSelectedCountry}
          minRating={minRating}
          onRatingChange={setMinRating}
          sortBy={sortBy}
          onSortChange={setSortBy}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          countries={countries}
          activeFiltersCount={activeFiltersCount}
          onClearFilters={handleClearFilters}
          showFavoritesOnly={showFavoritesOnly}
          onToggleFavoritesOnly={setShowFavoritesOnly}
          handleAddSupplier={handleAddSupplier}
          selectedSuppliersIds={localSelectedSuppliers}
          suppliers={suppliers}
          clientId={mantisBusiness?._id}
          orderId={data?.id}
          clientName={mantisBusiness?.name}
          onRFQSent={handleRFQSent}
        />
      </div>
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <div className="px-8 py-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Available Suppliers
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {filteredAndSortedSuppliers.length} suppliers available
          </p>
        </div>
        <div className="h-64 overflow-y-auto p-4">
          {filteredAndSortedSuppliers.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              No suppliers found
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {currentSuppliers.map((supplier) => (
                <SimpleSupplierCard
                  key={supplier.id}
                  supplier={supplier}
                  isSelected={selectedSuppliersIds.includes(supplier.id)}
                  onToggle={(supplierId) => {
                    // Toggle de supplier en el array de seleccionados
                    const newSelection = selectedSuppliersIds.includes(
                      supplierId
                    )
                      ? selectedSuppliersIds.filter((id) => id !== supplierId)
                      : [...selectedSuppliersIds, supplierId];

                    // Si es la primera vez que selecciona este supplier, inicializa con todos los items
                    if (!selectedSuppliersIds.includes(supplierId)) {
                      setSupplierItemsSelection((prev) => ({
                        ...prev,
                        [supplierId]: new Set(itemsData.map((item) => item.id)),
                      }));
                    } else {
                      // Si está deseleccionando el supplier, limpia su selección de items
                      setSupplierItemsSelection((prev) => {
                        const newSelection = { ...prev };
                        delete newSelection[supplierId];
                        return newSelection;
                      });
                    }

                    onSuppliersSelect(newSelection);
                  }}
                  assignedItemsCount={
                    supplierItemsSelection[supplier.id]?.size || 0
                  }
                />
              ))}
            </div>
          )}
          {/* Simple Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Tabla principal horizontal como en la foto */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <div className="px-8 py-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700">
          <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Order Items Assignment
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Assign items to selected suppliers
          </p>
        </div>

        <div className="h-[500px] overflow-auto p-6">
          {selectedSuppliersIds.length === 0 ? (
            <div className="flex items-center justify-center h-full text-slate-400 text-lg font-medium">
              Selecciona proveedores para asignar items
            </div>
          ) : (
            <Table className="w-full">
              <TableHeader className="bg-slate-50 dark:bg-slate-800/50 sticky top-0 z-10">
                <TableRow>
                  <TableHead className="text-left font-semibold text-slate-700 dark:text-slate-300 w-1/3">
                    Item
                  </TableHead>
                  {selectedSuppliersIds.map((supplierId) => {
                    const supplier = suppliers.find((s) => s.id === supplierId);
                    return (
                      <TableHead
                        key={supplierId}
                        className="text-center font-semibold text-slate-700 dark:text-slate-300 min-w-32"
                      >
                        <div className="flex flex-col items-center space-y-2">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                              <CheckSquare className="h-4 w-4 text-green-600" />
                            </div>
                            <span className="font-medium">
                              {supplier?.name || "Unknown"}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {supplier?.country || "Unknown"}
                          </div>
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {itemsData.map((item) => (
                  <TableRow
                    key={item.id}
                    className="hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors"
                  >
                    <TableCell className="text-left">
                      <div className="font-medium text-base text-slate-800 dark:text-slate-100">
                        {item.description || item.name || `Item ${item.id}`}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {item.quantity || 1} units
                      </div>
                    </TableCell>
                    {selectedSuppliersIds.map((supplierId) => (
                      <TableCell
                        key={`${item.id}-${supplierId}`}
                        className="text-center"
                      >
                        <Checkbox
                          checked={
                            supplierItemsSelection[supplierId]?.has(item.id) ||
                            false
                          }
                          onCheckedChange={(checked) => {
                            setSupplierItemsSelection((prev) => {
                              const currentSet = new Set(
                                prev[supplierId] || []
                              );
                              if (checked) {
                                currentSet.add(item.id);
                              } else {
                                currentSet.delete(item.id);
                              }
                              return { ...prev, [supplierId]: currentSet };
                            });
                          }}
                          className="h-10 w-10 border-2 border-slate-300 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600 data-[state=checked]:text-white hover:border-green-400 transition-colors rounded-full"
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Available Suppliers - Sección separada abajo */}
    </div>
  );
};

export default connect(selectAuthState)(SuppliersSection);

